import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faBuilding, faSync, faCalendarAlt, faInfoCircle, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const CorporateActions = ({ symbol }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (symbol) {
      loadCorporateActions();
    }
  }, [symbol]);

  const loadCorporateActions = async () => {
    try {
      setLoading(true);
      
      // Past 6 months is enough for the panel and avoids Alpaca timeouts on huge scans
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);
      
      const response = await marketDataApi.getCorporateActions(symbol, {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0],
        types: 'cash_dividend,stock_dividend,forward_split,reverse_split,spin_off',
      });
      
      if (response.success && response.data) {
        setActions(Array.isArray(response.data) ? response.data : []);
      } else {
        setActions([]);
      }
    } catch (err) {
      console.error('Error loading corporate actions:', err);
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const getActionTypeColor = (type) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower.includes('dividend')) return 'bg-green-100 text-green-800';
    if (typeLower.includes('split')) return 'bg-blue-100 text-blue-800';
    if (typeLower.includes('merger')) return 'bg-purple-100 text-purple-800';
    if (typeLower.includes('spinoff')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-slate-100 text-slate-800';
  };

  const getActionTypeLabel = (type) => {
    if (!type) return 'Corporate Action';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Don't reserve page space when there are no actions (or still loading / failed)
  if (actions.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-slate-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-slate-800 flex items-center">
          <FontAwesomeIcon icon={faBuilding} className="mr-2 text-slate-600" />
          Corporate Actions
        </h3>
        <button
          onClick={loadCorporateActions}
          disabled={loading}
          className="px-3 py-1.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium border border-slate-600"
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
          ) : (
            <>
              <FontAwesomeIcon icon={faSync} className="mr-1" />
              Refresh
            </>
          )}
        </button>
      </div>

      <div className="space-y-4">
        {actions.map((action, index) => (
          <div
            key={action.id || index}
            className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionTypeColor(action.type)}`}>
                  {getActionTypeLabel(action.type)}
                </span>
                {action.date && (
                  <div className="flex items-center text-sm text-slate-600">
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                    <span>Date: {formatDate(action.date)}</span>
                  </div>
                )}
              </div>
            </div>

            {action.description && (
              <p className="text-sm text-slate-700 mb-3">{action.description}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              {action.exDate && (
                <div className="text-sm">
                  <span className="text-slate-600 font-medium">Ex-Date:</span>
                  <span className="ml-2 text-slate-800">{formatDate(action.exDate)}</span>
                </div>
              )}
              {action.recordDate && (
                <div className="text-sm">
                  <span className="text-slate-600 font-medium">Record Date:</span>
                  <span className="ml-2 text-slate-800">{formatDate(action.recordDate)}</span>
                </div>
              )}
              {action.payableDate && (
                <div className="text-sm">
                  <span className="text-slate-600 font-medium">Payable Date:</span>
                  <span className="ml-2 text-slate-800">{formatDate(action.payableDate)}</span>
                </div>
              )}
            </div>

            {(action.cash || action.oldRate || action.newRate) && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="flex items-center space-x-4 text-sm">
                  {action.cash && (
                    <div className="flex items-center text-green-700">
                      <FontAwesomeIcon icon={faDollarSign} className="mr-1" />
                      <span className="font-medium">Cash: {formatCurrency(action.cash)}</span>
                    </div>
                  )}
                  {action.oldRate && (
                    <div className="text-slate-600">
                      <span className="font-medium">Old Rate:</span> {action.oldRate}
                    </div>
                  )}
                  {action.newRate && (
                    <div className="text-slate-600">
                      <span className="font-medium">New Rate:</span> {action.newRate}
                    </div>
                  )}
                </div>
              </div>
            )}

            {action.additionalDetails && Object.keys(action.additionalDetails).length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-600 hover:text-slate-800 flex items-center">
                    <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                    Additional Details
                  </summary>
                  <div className="mt-2 pl-4 space-y-1">
                    {Object.entries(action.additionalDetails).map(([key, value]) => (
                      <div key={key} className="text-slate-600">
                        <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                        <span className="text-slate-800">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CorporateActions;


